import { Link } from "react-router-dom";
import { useContent } from "@/hooks/useContent";
import { Monitor, Shield, Globe, Smartphone, Cloud, Brain, Layers, Handshake, DatabaseBackup, Users, Settings, Mail, FileText, Scale, Briefcase, ShieldCheck, FolderOpen, type LucideIcon } from "lucide-react";

type FooterLink = {
  label: string;
  href: string;
  isRoute?: boolean;
  icon: LucideIcon;
};

const footerLinks: { services: FooterLink[]; company: FooterLink[] } = {
  services: [
    { label: "IT Support", href: "/services/it-support", isRoute: true, icon: Monitor },
    { label: "Cyber Security", href: "/services/cyber-security", isRoute: true, icon: Shield },
    { label: "Web Development", href: "/services/web-development", isRoute: true, icon: Globe },
    { label: "Mobile Apps", href: "/services/mobile-app-development", isRoute: true, icon: Smartphone },
    { label: "Cloud Solutions", href: "/services/cloud-solutions", isRoute: true, icon: Cloud },
    { label: "Managed Solutions", href: "/services/managed-solutions-provider", isRoute: true, icon: Layers },
    { label: "Vendor Management", href: "/services/vendor-management", isRoute: true, icon: Handshake },
    { label: "Disaster Recovery", href: "/services/disaster-recovery", isRoute: true, icon: DatabaseBackup },
    { label: "AI Integration", href: "/services/ai-integration", isRoute: true, icon: Brain },
  ],
  company: [
    { label: "About Us", href: "/about-us", isRoute: true, icon: Users },
    { label: "Our Work", href: "/our-work", isRoute: true, icon: FolderOpen },
    { label: "Careers", href: "/careers", isRoute: true, icon: Briefcase },
    { label: "Our Process", href: "/#process", icon: Settings },
    { label: "Contact Us", href: "/contact", isRoute: true, icon: Mail },
    { label: "Privacy Policy", href: "/privacy-policy", isRoute: true, icon: FileText },
    { label: "Terms of Service", href: "/terms-of-service", isRoute: true, icon: Scale },
    { label: "Your Privacy Choices", href: "/your-privacy-choices", isRoute: true, icon: ShieldCheck },
  ],
};

const FooterLinkItem = ({ link }: { link: FooterLink }) => {
  const Icon = link.icon;
  const content = (
    <span className="flex items-center gap-2 text-sm text-muted-foreground transition-colors group-hover:text-primary">
      <Icon size={16} className="shrink-0" />
      {link.label}
    </span>
  );

  return link.isRoute ? (
    <Link to={link.href} className="group">
      {content}
    </Link>
  ) : (
    <a href={link.href} className="group">
      {content}
    </a>
  );
};

const SectionHeading = ({ children }: { children: React.ReactNode }) => (
  <div className="mb-5">
    <h4 className="font-display font-semibold text-sm uppercase tracking-wider">{children}</h4>
    <span className="block mt-1.5 w-8 h-0.5 bg-primary rounded-full" />
  </div>
);

const Footer = () => {
  const tagline = useContent("footer.tagline", "We bring technology to life. Your trusted partner for IT solutions, security, and digital transformation.");
  const copyright = useContent("footer.copyright", "Smartly Technologies. All rights reserved.");
  const brandName1 = useContent("nav.brand_name1", "Smartly");
  const brandName2 = useContent("nav.brand_name2", "Technologies");
  return (
    <footer className="bg-background text-foreground border-t border-border py-16">
      <div className="container mx-auto px-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-12 mb-12">
          {/* Brand */}
          <div>
            <a href="#" className="font-display text-xl font-bold tracking-tight">
              <span className="text-white">{brandName1}</span> <span className="text-primary">{brandName2}</span>
            </a>
            <p className="text-sm text-muted-foreground mt-4 max-w-xs">{tagline}</p>
          </div>

          {/* Services */}
          <div>
            <SectionHeading>Services</SectionHeading>
            <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-3">
              {footerLinks.services.map((l) => (
                <li key={l.label}>
                  <FooterLinkItem link={l} />
                </li>
              ))}
            </ul>
          </div>

          {/* Company */}
          <div>
            <SectionHeading>Company</SectionHeading>
            <ul className="space-y-3">
              {footerLinks.company.map((l) => (
                <li key={l.label}>
                  <FooterLinkItem link={l} />
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div className="border-t border-border pt-8 text-center">
          <p className="text-sm text-muted-foreground">
            © {new Date().getFullYear()} {copyright}
          </p>
        </div>
      </div>
    </footer>
  );
};
export default Footer;
